import { useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';

const modes = ['asc', 'desc'];

const defaultOptions = () => ({
  mode: 'desc',
  step: 1000,
  delay: 2000,
});

/**
 * Hook personalizado que implementa un debounce nativo con conteo visible.
 *
 * Permite ejecutar una acción tras un tiempo determinado (`delay`), mientras
 * actualiza un contador ascendente o descendente visible durante la espera.
 *
 * También soporta cancelación manual antes de que la acción se resuelva.
 *
 * ---
 * 🔹 Ejemplo de uso:
 * ```jsx
 * const { triggerAction, cancelAction, isCounting, count } = useNativeDebounce(5000, { mode: 'desc' });
 *
 * const handleClick = async () => {
 *   try {
 *     await triggerAction();
 *     console.debug('Acción ejecutada después del conteo');
 *   } catch (err) {
 *     console.warn(err.message);
 *   }
 * };
 * ```
 *
 * @param {Object} [entryOptions] - Opciones de configuración adicionales.
 * @param {'asc'|'desc'} [entryOptions.mode='desc'] - Sentido del conteo: `'asc'` (0→N) o `'desc'` (N→0).
 * @param {number} [entryOptions.step=1000] - Intervalo de actualización del contador (en milisegundos).
 * @param {number} [entryOptions.delay=2000] - Tiempo total del debounce en milisegundos.
 *
 * @returns {Object} - API del hook
 * @returns {() => Promise<boolean>} return.triggerAction - Inicia el conteo y devuelve una promesa que se resuelve al finalizar.
 * @returns {() => void} return.cancelAction - Cancela el conteo actual y rechaza la promesa pendiente.
 * @returns {boolean} return.isCounting - Indica si el conteo está activo.
 * @returns {number} return.count - Valor actual del contador visible (segundos).
 */
// eslint-disable-next-line default-param-last
export const useNativeDebounce = (entryOptions) => {
  const options = useMemo(() => _.merge({}, defaultOptions(), entryOptions), [entryOptions]);

  if (!modes.includes(options.mode))
    throw new Error('useNativeDebounce has invalid mode', options.mode);

  const timerRef = useRef(null);
  const intervalRef = useRef(null);
  const rejectRef = useRef(null);

  const [isCounting, setIsCounting] = useState(false);
  const [count, setCount] = useState(options.mode === 'asc' ? 0 : options.delay / 1000);

  const clearCounters = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const triggerAction = (threshold) => {
    return new Promise((resolve, reject) => {
      const timeout = typeof threshold !== 'number' ? options.delay : threshold;
      rejectRef.current = reject;

      if (timerRef.current) clearTimeout(timerRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);

      setIsCounting(true);
      setCount(options.mode === 'asc' ? 0 : timeout / 1000);

      // Inicia contador
      intervalRef.current = setInterval(() => {
        setCount((prev) => {
          const next =
            options.mode === 'asc' ? prev + options.step / 1000 : prev - options.step / 1000;

          if (
            (options.mode === 'asc' && next * 1000 >= timeout) ||
            (options.mode === 'desc' && next <= 0)
          ) {
            clearInterval(intervalRef.current);
          }

          return Math.max(0, next);
        });
      }, options.step);

      // ---- Inicia conteo principal
      timerRef.current = setTimeout(() => {
        clearCounters();
        setIsCounting(false);
        resolve(true);
      }, timeout);
    });
  };

  const cancelAction = () => {
    if (!isCounting) return;
    clearCounters();

    setIsCounting(false);

    if (rejectRef.current) {
      rejectRef.current(new Error('Acción cancelada por el usuario'));
      rejectRef.current = null;
    }
  };

  useEffect(() => {
    clearCounters();
    return () => {
      clearCounters();
      if (rejectRef.current) rejectRef.current(new Error('Hook desmontado'));
    };
  }, []);

  return {
    triggerAction,
    cancelAction,
    isCounting,
    count,
  };
};
